<template>
  <div class="holder-table">
    <AlertErrorMsg
      ref="alert"
      :title="errorMessage"
      class="page-alert-error"
    />
    <TableHeader v-if="sessionsData.length > 0">
      <div class="email">
        <div class="table-header-box">
          <EmailIcon />
          <span class="header-table-text">Email</span>
        </div>
      </div>

      <div class="device">
        <div class="table-header-box">
          <DevicesIcon16 />
          <span class="header-table-text">Device</span>
        </div>
      </div>

      <div class="session">
        <div
          class="table-header-box"
        >
          <CaseIcon />
          <span class="header-table-text">Session</span>
        </div>
      </div>

      <div class="started">
        <div
          id="sort_users_sessions_by_start_date"
          class="table-header-box"
          :class="{
            'sort-active': this.sortBy === 'started',
            'sorted-desc': this.sortOrder === 'desc',
          }"
          @click="sortedBy('started')"
        >
          <DateIcon16 />
          <span class="header-table-text">Started</span>
          <arrowDown class="arrow" />
        </div>
      </div>
      <div class="finished">
        <div
          id="sort_users_sessions_by_finish_date"
          class="table-header-box"
          :class="{
            'sort-active': this.sortBy === 'finished',
            'sorted-desc': this.sortOrder === 'desc',
          }"
          @click="sortedBy('finished')"
        >
          <DateIcon16 />
          <span class="header-table-text">Finished</span>
          <arrowDown class="arrow" />
        </div>
      </div>
    </TableHeader>

    <div v-if="sessionsData.length > 0" class="table-body">
      <div
        v-for="(session, sessionIndex) in updatedSessions"
        :key="sessionIndex"
        class="table-row"
      >
        <CustodianSessionItem
          :session="session"
          @activeSession="onSessionClick(session, sessionIndex)"
        />

        <transition name="slide">
          <div v-if="session.isActive && session.screenshots[0]" class="session-details-wrapper">
            <div class="session-timeline-wrapper">
              <input
                v-model="session.screenPage"
                type="range"
                min="0"
                :max="getDefaultDevice(session.screenshots[0]).length - 5"
                step="any"
              >
              <div class="events">
                <span
                  v-for="(item, index) in getDefaultDevice(session.screenshots[0])"
                  :key="index"
                  :data-event="index"
                >
                  {{ reformatTime(item.createdAt) }}
                </span>
              </div>
            </div>
            <div class="session-screens-wrapper">
              <div
                class="back-button"
                :class="{ 'disabled': Math.round(session.screenPage) === 0 }"
                @click="prevPage(session)"
              >
                <arrowDown class="arrow" />
              </div>
              <div class="holders-wrapper">
                <div v-if="session.screenshots[0].device" class="device-session-screens-holder">
                  <div
                    v-for="(item, index) in paginatedEvents(session.screenshots[0].device, session.screenPage)"
                    :key="index"
                    class="screen-wrapper"
                  >
                    <div class="screen-header">
                      <div class="screen-header-text">
                        {{ reformatTime(item.createdAt) }}
                      </div>
                      <GreyOpenedEyeIcon30 @click="openImgPopup(session, session.screenshots[0].device, item)" />
                    </div>
                    <div class="screen-holder">
                      <div class="screen-cover" @click="openImgPopup(session, session.screenshots[0].device, item)">
                        <WhiteOpenEyeIcon50 />
                      </div>
                      <img :src="item.linkPreview" alt="screen">
                    </div>
                  </div>
                </div>
                <div v-if="session.screenshots[0].desktop" class="desktop-session-screens-holder">
                  <div
                    v-for="(item, index) in paginatedEvents(session.screenshots[0].desktop, session.screenPage)"
                    :key="index"
                    class="screen-wrapper"
                  >
                    <div class="screen-header">
                      <div class="screen-header-text">
                        {{ reformatTime(item.createdAt) }}
                      </div>
                      <GreyOpenedEyeIcon30 @click="openImgPopup(session, session.screenshots[0].device, item)" />
                    </div>
                    <div class="screen-holder">
                      <div class="screen-cover" @click="openImgPopup(session, session.screenshots[0].desktop, item)">
                        <WhiteOpenEyeIcon50 />
                      </div>
                      <img :src="item.linkPreview" alt="screen">
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="forward-button"
                :class="{
                  'disabled': Math.round(session.screenPage) >= getDefaultDevice(session.screenshots[0]).length - 5
                }"
                @click="nextPage(session)"
              >
                <arrowDown class="arrow" />
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
    <div v-else-if="sessionsData.length === 0" class="search-cases">
      <h3 class="no-found">
        No sessions found
      </h3>
    </div>
    <div v-else class="search-cases">
      <h3>Start by choosing a case</h3>
    </div>
    <screenshot-popup ref="imagePopup" :session="currentSession" />
  </div>
</template>

<script>
import TableHeader from '../table/TableHeader';
import AlertErrorMsg from '../profile/AlertErrorMsg';
import ArrowDown from '../icons/ArrowDown';
import EmailIcon from '../icons/EmailIcon';
import ScreenshotPopup from '@/components/popups/ScreenshotPopup.vue';
import reformatDate from '@/mixins/reformatDate';
import reformatDateToTime from '@/mixins/reformatDateToTime';
import CustodianSessionItem from '@/components/support/CustodianSessionItem.vue';
import errorHandler from '@/mixins/errorHandler';
import DevicesIcon16 from '@/components/icons/DevicesIcon16.vue';
import CaseIcon from '@/components/icons/CaseIcon.vue';
import DateIcon16 from '@/components/icons/DateIcon16.vue';
import GreyOpenedEyeIcon30 from '@/components/icons/GreyOpenedEyeIcon30.vue';
import WhiteOpenEyeIcon50 from '@/components/icons/WhiteOpenEyeIcon50.vue';

export default {
  name: 'CustodianSessionsList',
  components: {
    WhiteOpenEyeIcon50,
    GreyOpenedEyeIcon30,
    DateIcon16,
    CaseIcon,
    DevicesIcon16,
    CustodianSessionItem,
    ScreenshotPopup,
    EmailIcon,
    ArrowDown,
    TableHeader,
    AlertErrorMsg,
  },
  emits: ['sorting'],
  mixins: [reformatDate, reformatDateToTime, errorHandler],
  props: {
    sessionsData: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      sortBy: 'email',
      sortOrder: 'asc',
      loadFlag: false,
      errorMessage: '',
      updatedSessions: [],
      currentSession: {
        currentScreenshot: null,
        email: '',
        device: '',
        sessionId: '',
        screenPage: 0,
      },
    };
  },
  watch: {
    sessionsData: {
      immediate: true,
      deep: true,
      handler(newSessionsData) {
        if (newSessionsData) {
          this.updatedSessions = newSessionsData.map(session => ({
            ...session,
            isActive: false,
            screenPage: 0
          }));
        }
      }
    }
  },
  methods: {
    openImgPopup(session, sessionScreenshots, screenshot) {
      this.currentSession = {
        ...session,
        screenshots: sessionScreenshots,
        screenPage: sessionScreenshots.indexOf(screenshot),
        currentScreenshot: screenshot,
      };
      this.$refs.imagePopup.openPopups();
    },
    paginatedEvents(sessionScreenshots, sessionScreenPage) {
      if (sessionScreenshots.length <= 5) {
        return sessionScreenshots;
      }
      return sessionScreenshots.slice(Math.round(sessionScreenPage), Math.round(sessionScreenPage) + 5);
    },
    sortedBy(filterName) {
      this.sortOrder = filterName === this.sortBy && this.sortOrder === 'desc' ? 'asc' : 'desc';
      this.sortBy = filterName;
      this.$emit('sorting', {sortOrder: this.sortOrder, sortBy: this.sortBy});
    },
    nextPage(session) {
      if (session.screenPage >= this.getDefaultDevice(session.screenshots[0]).length - 6
        && session.screenPage <= this.getDefaultDevice(session.screenshots[0]).length - 5) {
        session.screenPage = this.getDefaultDevice(session.screenshots[0]).length - 5;
      } else {
        session.screenPage++;
      }
    },
    prevPage(session) {
      if (session.screenPage >= 0 && session.screenPage < 1) {
        session.screenPage = 0;
      } else {
        session.screenPage--;
      }
    },
    onSessionClick(currentSession, index) {
      const updatedSession = {
        ...currentSession,
        screenshots: this.getSessionScreenshots(currentSession.sessionId),
        isActive: this.updatedSessions[index] ? !this.updatedSessions[index].isActive : true
      };
      this.$set(this.updatedSessions, index, updatedSession);
    },
    getSessionScreenshots(sessionId) {
      const screenshots = [];
      this.$load(async () => {
        try {
          const res = await this.$api.screenshots.getScreenshots(this.$route.params.slug, sessionId);
          screenshots.push(res.data.body);
        } catch (error) {
          this.handleResponseError(error);
          return [];
        }
      });
      return screenshots;
    },
    getDefaultDevice(sessionScreenshots) {
      if (sessionScreenshots.device && sessionScreenshots.desktop) {
        if (sessionScreenshots.device.length > sessionScreenshots.desktop.length) {
          return sessionScreenshots.device;
        } else {
          return sessionScreenshots.desktop;
        }
      }
      return sessionScreenshots.device
        ? sessionScreenshots.device
        : sessionScreenshots.desktop
          ? sessionScreenshots.desktop
          : [];
    },
  },
};
</script>

<style lang="scss" scoped>

.holder-table {
  margin: 15px 0 0;
}

.table-header {
  max-height: 64px;
  height: 64px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 15px 0 15px 22px;
}

.table-header-box svg path {
  fill: var(--input-placeholder-value-color);
  transition: all 0.3s;
}

.table-header-box:hover svg:first-child path {
  fill: var(--accent-color);
}

.email {
  width: 18%;
}

.device {
  width: 25%;
}

.session {
  width: 15%;
}

.started {
  width: 18%;
}

.finished {
  width: 20%;
}

.dialog-message-title {
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 36px;
  color: #292B2F;
  text-align: center;
  margin: 25px 0;
  display: block;
  width: 100%;
}

.dialog-message-description {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 26px;
  text-align: center;
  color: #696F79;
  display: block;
  width: 100%;
  margin-top: 20px;
}

.arrow {
  transition: transform 0.3s;
}

.session-details-wrapper {
  height: fit-content;
}

.session-timeline-wrapper {
  height: 45px;
  margin: 32px 30px 15px 45px;
}

input[type="range"] {
  width: 100%;
  appearance: none;
  border: none;
  height: 25px;
}

input[type="range"]::-webkit-slider-runnable-track {
  width: 100%;
  height: 8px;
  background: var(--case-btn-border-color);
}

input[type="range"]::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  background: var(--case-card-hover-bg);
  border-radius: 50%;
  cursor: pointer;
  margin: -6px 0 0;
  position: relative;
  z-index: 2;
  border: 3px solid var(--light-gray);
}

.events {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0 20px;
}

.events span {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  color: var(--secondary-black-color);
}

.events span::before {
  content: "";
  width: 3px;
  height: 13px;
  background-color: var(--light-gray);
  position: absolute;
  transform: translate(0, -175%);
}

.session-screens-wrapper {
  display: flex;
  height: fit-content;
  margin-bottom: 15px;
}

.holders-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 74vw;
}

.back-button,
.forward-button {
  width: 37px;
  height: 81px;
  padding: 30px 8px;
  border-radius: 8px;
  border: 1px solid #EBEFF6;
  cursor: pointer;
}

.back-button .arrow {
  width: 21px;
  height: 21px;
  transform: rotate(90deg);
}

.forward-button .arrow {
  width: 21px;
  height: 21px;
  transform: rotate(-90deg);
}

.device-session-screens-holder {
  display: flex;
  height: 420px;
  border-radius: 8px;
  border: 1px solid #EBEFF6;
  margin: 0 8px;
}

.desktop-session-screens-holder {
  display: flex;
  height: 180px;
  border-radius: 8px;
  border: 1px solid #EBEFF6;
  margin: 0 8px;
}

.screen-wrapper {
  display: flex;
  flex-direction: column;
  width: 20%;
  gap: 16px;
  padding: 24px 40px;
  border-right: 1px solid #EBEFF6;
}

.screen-wrapper:last-child {
  border-right: none;
}

.screen-header {
  display: flex;
  justify-content: space-between;
}

.screen-header-text {
  font-size: 14px;
  font-weight: 600;
  line-height: 16.94px;
  text-align: left;
  color: var(--secondary-black-color);
}

.screen-header svg {
  cursor: pointer;
}

.screen-holder {
  border-radius: 8px;
  cursor: pointer;
  width: 178px;
  height: auto;
  overflow: hidden;
}

.screen-holder img {
  max-width: 100%;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.device-session-screens-holder .screen-holder {
  height: 330px;
}

.desktop-session-screens-holder .screen-holder {
  height: 90px;
}

.screen-cover {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 100;
  width: inherit;
  height: inherit;
  transition: all 0.7s ease;
  border-radius: 8px;
  opacity: 0;
}

.screen-cover:hover {
  opacity: 100;
  background: #00000080 50%;
}

.disabled {
  opacity: 0.2;
}


.no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70vh;
}

.search-cases h3 {
  font-size: 16px;
  font-weight: 500;
  line-height: 25px;
  text-align: left;
  color: var(--light-gray);
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter, .slide-leave-to {
  height: 0;
  opacity: 0;
  overflow: hidden;
}

</style>
